import { useEffect, useState } from "react";

import { Menu } from "lucide-react";
import { Outlet, useLocation } from "react-router-dom";

import { Sidebar } from "@/components/layout/Sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useReports } from "@/hooks/useReports";
import { useI18n } from "@/i18n/useI18n";

/** Page titles, keyed by route, as catalogue keys. The top bar renders these in gradient text. */
const TITLE_KEYS: Record<string, string> = {
  "/": "nav.reports",
  "/admin/users": "nav.users",
  "/admin/access": "nav.reportAccess",
  "/admin/companies": "nav.companies",
};

export function AppLayout() {
  const { user } = useAuth();
  const { reports } = useReports();
  const { t } = useI18n();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes — otherwise tapping a link on a
  // phone leaves the drawer covering the page you just navigated to.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const report = reports.find((r) => location.pathname === `/reports/${r.key}`);
  // ⚠ A report's own name is a proper noun — the Monday board is called Quality Audits in
  // both languages — so it comes from the catalogue, untranslated.
  const titleKey = TITLE_KEYS[location.pathname];
  const title = report?.name ?? (titleKey ? t(titleKey) : "Quality Field");

  const initials =
    user?.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "?";

  const roleLabel =
    user?.role === "SYSTEM_ADMIN"
      ? t("role.admin")
      : user?.role === "UNIFIBER_INTERNAL"
        ? t("role.internal")
        : // A contractor is named by their company, which is a proper noun.
          (user?.companyName ?? "");

  return (
    <>
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} reports={reports} />

      <div className="min-h-screen bg-slate-50 lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
              className="-ml-2 rounded-lg p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-800 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="gradient-text truncate text-xl font-bold lg:text-2xl">{title}</h1>
            <span className="hidden whitespace-nowrap text-xs font-medium text-slate-400 xl:inline">
              {/* ⚠ One sentence with a placeholder, split on a SENTINEL rather than on the
                  value itself: the role can be an empty string (a contractor whose company
                  has no name yet), and splitting on "" would explode the sentence into
                  characters. The role does not sit in the same place in every language,
                  which is why this is not two fragments either side of a value. */}
              {(() => {
                const [before, after] = t("shell.signedInAs", { role: "\u0000" }).split("\u0000");
                return (
                  <>
                    {before}
                    <span className="font-semibold text-brand-purple">{roleLabel}</span>
                    {after}
                  </>
                );
              })()}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-2 lg:gap-4">
            {/* ⚠ The mockup's search box and notification bell are deliberately NOT here
                yet. Neither has anything behind it until the reports land, and a control
                that does nothing teaches people the product is broken. They come back with
                the report whose search and snapshot notifications justify them. */}
            <div
              className="gradient-band flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
              title={user?.email}
            >
              {initials}
            </div>
          </div>
        </header>

        <main>
          <Outlet />
        </main>
      </div>
    </>
  );
}
